import type { Edge, Node } from '../../../core/models/scene';
import { isOrgNodeType } from '../../../domains/orgchart/constants/orgNodeTypes';

type OrgTreeModeDragInput = {
  diagramType?: string;
  autoLayoutEnabled?: boolean;
  node?: (Pick<Node, 'id' | 'type' | 'attrs' | 'props'> & { dataBinding?: unknown }) | null;
  nodes?: readonly (Pick<Node, 'id' | 'attrs' | 'props'> & { dataBinding?: unknown })[];
  edges: readonly Edge[];
  sourceBacked?: boolean;
};

export function shouldRestoreOrgTreeModeDrop(input: OrgTreeModeDragInput): boolean {
  if (input.diagramType !== 'org') return false;
  if (!input.autoLayoutEnabled) return false;
  if (!input.node || !isOrgNodeType(input.node.type as string | undefined)) return false;

  const primaryReportingEdges = input.edges.filter((edge) => {
    if (!isPrimaryReportingEdge(edge)) return false;
    return edge.from === input.node?.id || edge.to === input.node?.id;
  });
  if (primaryReportingEdges.length === 0) return false;

  return Boolean(
    input.sourceBacked ||
    hasSourceBackedOrgMarker(input.node) ||
    primaryReportingEdges.some((edge) => hasLegacyReportingMarker(input.node!, edge, input.nodes ?? []))
  );
}

function isPrimaryReportingEdge(edge: Edge): boolean {
  const attrs = (edge.attrs ?? {}) as Record<string, unknown>;
  if (attrs.dotted === true) return false;
  const edgeType = String(edge.type ?? '');
  const relation = typeof attrs.relation === 'string' ? attrs.relation : undefined;
  return edgeType === 'reporting' || edgeType === 'reports_to' || relation === 'reporting' || relation === 'reports_to';
}

function hasSourceBackedOrgMarker(node: Pick<Node, 'attrs' | 'props'> & { dataBinding?: unknown }): boolean {
  return Boolean(
    hasBinding(node.dataBinding) ||
    hasBinding((node.props as Record<string, unknown> | undefined)?.dataBinding) ||
    hasBinding((node.attrs as Record<string, unknown> | undefined)?.dataBinding) ||
    hasNonEmptyString((node.attrs as Record<string, unknown> | undefined)?.folderRecordId) ||
    hasNonEmptyString((node.props as Record<string, unknown> | undefined)?.folderRecordId) ||
    hasNonEmptyString((node.attrs as Record<string, unknown> | undefined)?.importedFrom) ||
    hasNonEmptyString((node.attrs as Record<string, unknown> | undefined)?.sourceId)
  );
}

function hasLegacyReportingMarker(
  node: Pick<Node, 'id' | 'attrs' | 'props'>,
  edge: Edge,
  nodes: readonly Pick<Node, 'id' | 'attrs' | 'props'>[],
): boolean {
  if (edge.from === node.id) {
    return reportsToNode(node, edge.to);
  }
  if (edge.to === node.id) {
    const childNode = nodes.find((candidate) => candidate.id === edge.from);
    return Boolean(childNode && reportsToNode(childNode, node.id));
  }
  return false;
}

function reportsToNode(node: Pick<Node, 'attrs' | 'props'>, managerNodeId: string): boolean {
  const attrs = (node.attrs ?? {}) as Record<string, unknown>;
  const props = (node.props ?? {}) as Record<string, unknown>;
  const managerId = firstNonEmptyString(
    attrs.managerId,
    props.managerId,
    attrs.reportsTo,
    props.reportsTo,
    attrs.reportsToId,
    props.reportsToId,
    attrs.parentId,
    props.parentId,
  );
  return managerId === managerNodeId;
}

function hasBinding(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const binding = value as Record<string, unknown>;
  return hasNonEmptyString(binding.sourceId) || hasNonEmptyString(binding.recordId);
}

function firstNonEmptyString(...values: unknown[]): string | undefined {
  const value = values.find(hasNonEmptyString);
  return typeof value === 'string' ? value.trim() : undefined;
}

function hasNonEmptyString(value: unknown): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}
